/**
 * =============================================================================
 * Componente: ModalTabsBar
 * Versão: 1.0.0
 * Release: 2026-05-02 — Padrão unificado de abas para modais de criação/edição
 *
 * Descrição:
 *   Barra de abas premium (h-9) usada dentro de modais de cadastro/edição.
 *   Reaproveita o mesmo visual já utilizado em DealModalTabs / TaskModalTabs /
 *   ReferralCampaignModalTabs.
 *
 *   API genérica baseada em configuração — não depende do conjunto de abas:
 *   cada tela passa seu próprio array tipado e este componente renderiza.
 *
 * Uso típico:
 *   const tabs: ModalTabConfig<MinhaTab>[] = [
 *     { id: 'general', label: 'Geral', icon: Info },
 *     { id: 'rules',   label: 'Regras', icon: Gavel, count: 3 },
 *   ];
 *   <ModalTabsBar tabs={tabs} activeTab={tab} onTabChange={setTab} />
 * =============================================================================
 */
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

/** Configuração de uma aba individual. */
export interface ModalTabConfig<TId extends string = string> {
  /** Identificador único da aba (string literal/union). */
  id: TId;
  /** Texto exibido. */
  label: string;
  /** Ícone (lucide-react). */
  icon: LucideIcon;
  /** Contador opcional exibido como badge. */
  count?: number;
  /** Indicador visual de erro de validação na aba. */
  hasError?: boolean;
  /** Desabilita o clique na aba. */
  disabled?: boolean;
}

interface ModalTabsBarProps<TId extends string> {
  /** Lista de abas a renderizar (na ordem). */
  tabs: ModalTabConfig<TId>[];
  /** Aba atualmente ativa. */
  activeTab: TId;
  /** Disparado ao clicar em uma aba habilitada. */
  onTabChange: (id: TId) => void;
  /** Classes adicionais para o container. */
  className?: string;
}

/**
 * Barra de abas (36px) padrão para modais premium.
 * - Mobile: scroll horizontal automático.
 * - Indicador inferior na aba ativa.
 * - Suporta contador (badge) e indicador de erro (bolinha vermelha).
 */
export function ModalTabsBar<TId extends string>({
  tabs,
  activeTab,
  onTabChange,
  className,
}: ModalTabsBarProps<TId>) {
  return (
    <div
      className={cn(
        'flex items-center h-9 px-2 md:px-4 border-b bg-muted/20 shrink-0 overflow-x-auto',
        className,
      )}
    >
      {tabs.map((tab) => {
        const Icon = tab.icon;
        const isActive = activeTab === tab.id;
        const hasError = !!tab.hasError;
        const isDisabled = !!tab.disabled;

        return (
          <button
            key={tab.id}
            type="button"
            disabled={isDisabled}
            onClick={() => !isDisabled && onTabChange(tab.id)}
            aria-current={isActive ? 'page' : undefined}
            className={cn(
              'flex items-center gap-1 md:gap-1.5 px-2 md:px-3 h-full text-[10px] md:text-xs font-medium transition-colors relative whitespace-nowrap',
              'hover:text-foreground',
              isActive ? 'text-primary' : 'text-muted-foreground',
              hasError && !isActive && 'text-destructive',
              isDisabled && 'opacity-40 cursor-not-allowed hover:text-muted-foreground',
            )}
          >
            <Icon className="h-3.5 w-3.5" />
            <span>{tab.label}</span>

            {/* Contador (badge) */}
            {tab.count !== undefined && tab.count > 0 && (
              <span
                className={cn(
                  'text-[10px] min-w-[16px] h-4 px-1 rounded-full flex items-center justify-center',
                  isActive
                    ? 'bg-primary/10 text-primary'
                    : 'bg-muted text-muted-foreground',
                )}
              >
                {tab.count}
              </span>
            )}

            {/* Indicador de erro (bolinha vermelha) */}
            {hasError && (
              <span className="h-1.5 w-1.5 rounded-full bg-destructive" />
            )}

            {/* Indicador inferior da aba ativa */}
            {isActive && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-t-full" />
            )}
          </button>
        );
      })}
    </div>
  );
}
